import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Plus, Edit2, Trash2, Mail, User, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmergencyResponse = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    role: 'Managing Committee Representative',
    name: '',
    phone: '',
    email: '',
    available: true,
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact}`, formData);
      } else {
        await axios.post(`${API}/contacts`, formData);
      }
      fetchContacts();
      resetForm();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`);
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const startEdit = (contact) => {
    setEditingContact(contact.id);
    setFormData({
      role: contact.role,
      name: contact.name,
      phone: contact.phone,
      email: contact.email || '',
      available: contact.available,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingContact(null);
    setFormData({
      role: 'Managing Committee Representative',
      name: '',
      phone: '',
      email: '',
      available: true,
    });
  };

  const roles = [
    'Managing Committee Representative',
    'Animal Welfare Volunteer',
    'Local Veterinarian',
    'Emergency Animal Ambulance',
    'Other'
  ];

  const getRoleColor = (role) => {
    switch (role) {
      case 'Managing Committee Representative': return 'from-blue-400 to-sky-500';
      case 'Animal Welfare Volunteer': return 'from-green-400 to-emerald-500';
      case 'Local Veterinarian': return 'from-purple-400 to-violet-500';
      case 'Emergency Animal Ambulance': return 'from-red-400 to-orange-500';
      default: return 'from-gray-400 to-slate-500';
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-red-500 to-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-white p-3 rounded-xl shadow-lg">
                    <Phone className="w-8 h-8 text-red-600" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-extrabold text-white font-heading" data-testid="emergency-title">
                    Emergency Response
                  </h1>
                </div>
                <p className="text-lg text-white/90 font-medium">Official contacts for animal welfare emergencies</p>
              </div>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  data-testid="add-contact-button"
                  className="bg-white text-red-600 px-6 py-3 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Add Contact
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-orange-100 p-8 mb-8"
          >
            <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    data-testid="input-role"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                    placeholder="Full name"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                    placeholder="+91 98765 43210"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                    placeholder="email@example.com"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>
              </div>
              <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-orange-100 bg-orange-50 cursor-pointer hover:border-primary transition-all">
                <input
                  type="checkbox"
                  name="available"
                  checked={formData.available}
                  onChange={handleChange}
                  data-testid="input-available"
                  className="w-5 h-5 text-primary rounded focus:ring-4 focus:ring-orange-100"
                />
                <span className="font-bold text-text-primary">Currently Available</span>
              </label>
              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="save-contact-button"
                  className="flex-1 bg-primary text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  {editingContact ? 'Update Contact' : 'Add Contact'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-button"
                  className="bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center py-12" data-testid="loading-indicator">
            <div className="text-xl text-text-secondary font-medium">Loading contacts...</div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12" data-testid="no-contacts">
            <Phone className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <div className="text-xl text-text-secondary font-medium mb-2">No contacts added yet</div>
            <p className="text-text-muted">Add emergency contacts to help residents quickly reach the right people</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contacts.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                data-testid={`contact-card-${contact.id}`}
                className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden hover:shadow-xl transition-all"
              >
                <div className={`bg-gradient-to-br ${getRoleColor(contact.role)} p-6`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1 font-heading" data-testid="contact-name">{contact.name}</h3>
                      <p className="text-white/90 text-sm font-medium" data-testid="contact-role">{contact.role}</p>
                    </div>
                    {contact.available ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-testid="contact-status">
                        Available
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid="contact-status">
                        Unavailable
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <a
                    href={`tel:${contact.phone}`}
                    data-testid="contact-phone-link"
                    className="flex items-center gap-3 p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-all group"
                  >
                    <div className="bg-green-600 p-2 rounded-lg">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-green-700 mb-1">CALL NOW</div>
                      <div className="text-lg font-bold text-text-primary group-hover:text-green-700 transition-colors" data-testid="contact-phone">
                        {contact.phone}
                      </div>
                    </div>
                  </a>
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      data-testid="contact-email-link"
                      className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 hover:bg-orange-100 transition-all"
                    >
                      <Mail className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium text-text-secondary" data-testid="contact-email">{contact.email}</span>
                    </a>
                  )}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => startEdit(contact)}
                      data-testid="edit-contact-button"
                      className="flex-1 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all font-bold flex items-center justify-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(contact.id)}
                      data-testid="delete-contact-button"
                      className="flex-1 px-4 py-2 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 transition-all font-bold flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-orange-200 p-8 mt-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-3 font-heading">Emergency Guidelines</h3>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>For immediate medical emergencies, call the Emergency Animal Ambulance first</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>For aggressive animal incidents, contact the Animal Welfare Volunteer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>For policy-related queries, reach out to the Managing Committee Representative</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>Keep these contacts readily available for quick response</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyResponse;